/** Pending-changes registry — "is there unsaved work on this page?"
 *
 * Anything that can redirect the user on its own (the Nucleus dock acting on a
 * tool's navigation) asks here first: a redirect over an unsaved edit throws
 * the edit away, so an automatic one is refused and the destination is offered
 * as a card instead. Pages/forms register a keyed flag while they hold unsaved
 * state (mirroring the beforeunload guards they already keep); the keys are
 * only for bookkeeping, never shown.
 *
 * Module-level (not React context) so it can be read outside render — the
 * dock consults it inside an async turn handler — and so embeds' postMessage
 * listeners can flag it too.
 */
import { useEffect } from "react";

const pending = new Map<string, string>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

/** Flag (or clear) unsaved work under `key`; `description` is for logs/UI. */
export const setPendingChanges = (key: string, isPending: boolean, description = key): void => {
  const had = pending.has(key);
  if (isPending) pending.set(key, description);
  else pending.delete(key);
  if (had !== isPending) notify();
};

export const hasPendingChanges = (): boolean => pending.size > 0;

/** Human-readable list of what is unsaved (for a "not redirected" warning). */
export const describePendingChanges = (): string[] => Array.from(pending.values());

export const subscribePendingChanges = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Keep the registry in step with a component's dirty flag; clears on unmount
 *  (an unmounted form has no unsaved state the user can still lose). */
export const usePendingChanges = (key: string, isPending: boolean, description?: string): void => {
  useEffect(() => {
    setPendingChanges(key, isPending, description);
    return () => setPendingChanges(key, false);
  }, [key, isPending, description]);
};

/** Test/support hook: drop every flag. */
export const _resetPendingChanges = (): void => {
  pending.clear();
  notify();
};
